import React, { useState } from 'react';

const AICodingBlocksGame = ({ gameData, onComplete }) => {
    const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [commands, setCommands] = useState([]);
    const [isExecuting, setIsExecuting] = useState(false);
    const [robotPos, setRobotPos] = useState(null);
    const [robotDir, setRobotDir] = useState(null);
    const [feedback, setFeedback] = useState(null);

    const currentLevel = gameData?.levels?.[currentLevelIndex];

    React.useEffect(() => {
        if (currentLevel) {
            setRobotPos({ x: currentLevel.robot_start.x, y: currentLevel.robot_start.y });
            setRobotDir(currentLevel.robot_start.direction);
        }
    }, [currentLevelIndex, currentLevel]);

    const addCommand = (cmd) => {
        if (commands.length < currentLevel.max_commands) {
            setCommands([...commands, cmd]);
            if (navigator.vibrate) navigator.vibrate(20);
        }
    };

    const removeLastCommand = () => {
        setCommands(commands.slice(0, -1));
        if (navigator.vibrate) navigator.vibrate(10);
    };

    const clearCommands = () => {
        setCommands([]);
        setRobotPos({ x: currentLevel.robot_start.x, y: currentLevel.robot_start.y });
        setRobotDir(currentLevel.robot_start.direction);
        if (navigator.vibrate) navigator.vibrate(30);
    };

    const executeCommands = async () => {
        setIsExecuting(true);
        setFeedback(null);

        let pos = { ...currentLevel.robot_start };
        let dir = currentLevel.robot_start.direction;

        for (const cmd of commands) {
            await new Promise(resolve => setTimeout(resolve, 600));
            if (navigator.vibrate) navigator.vibrate(40);

            if (cmd === 'turn_left') {
                dir = turnLeft(dir);
            } else if (cmd === 'turn_right') {
                dir = turnRight(dir);
            } else if (cmd === 'forward') {
                const newPos = moveForward(pos, dir);
                if (isValidMove(newPos)) {
                    pos = newPos;
                } else {
                    setFeedback({ type: 'error', message: 'Robotas atsitrenkė į sieną! 🚧' });
                    setIsExecuting(false);
                    if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
                    return;
                }
            }

            setRobotPos({ ...pos });
            setRobotDir(dir);
        }

        setIsExecuting(false);

        if (pos.x === currentLevel.goal.x && pos.y === currentLevel.goal.y) {
            setScore(prev => prev + (gameData.points_per_level || 25));
            setFeedback({ type: 'success', message: 'Tikslas pasiektas! 🌟' });
            playSuccessSound();
            if (navigator.vibrate) navigator.vibrate([100, 50, 100]);

            setTimeout(() => {
                nextLevel();
            }, 3000);
        } else {
            setFeedback({ type: 'error', message: 'Nepasiektas tikslas. Bandyk dar kartą! 🤖' });
            if (navigator.vibrate) navigator.vibrate([50, 50, 50]);
        }
    };

    const turnLeft = (dir) => {
        const dirs = ['up', 'left', 'down', 'right'];
        const idx = dirs.indexOf(dir);
        return dirs[(idx + 1) % 4];
    };

    const turnRight = (dir) => {
        const dirs = ['up', 'right', 'down', 'left'];
        const idx = dirs.indexOf(dir);
        return dirs[(idx + 1) % 4];
    };

    const moveForward = (pos, dir) => {
        const newPos = { ...pos };
        if (dir === 'up') newPos.y--;
        if (dir === 'down') newPos.y++;
        if (dir === 'left') newPos.x--;
        if (dir === 'right') newPos.x++;
        return newPos;
    };

    const isValidMove = (pos) => {
        if (pos.x < 0 || pos.x >= 5 || pos.y < 0 || pos.y >= 5) return false;
        if (currentLevel.grid[pos.y][pos.x] === 'X') return false;
        return true;
    };

    const nextLevel = () => {
        if (currentLevelIndex < gameData.levels.length - 1) {
            setCurrentLevelIndex(prev => prev + 1);
            setCommands([]);
            setFeedback(null);
        } else {
            onComplete?.(score);
        }
    };

    const playSuccessSound = () => {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
            const ctx = new AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = 'sine';
            osc.frequency.setValueAtTime(900, ctx.currentTime);
            osc.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.1);
            gain.gain.setValueAtTime(0.1, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.00001, ctx.currentTime + 0.3);
            osc.start();
            osc.stop(ctx.currentTime + 0.3);
        }
    };

    if (!gameData || !gameData.levels || !robotPos) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-cyan-100 animate-pulse">
                <div className="text-lg md:text-4xl md:text-[100px] mb-8">🤖</div>
                <h3 className="text-base md:text-3xl font-black text-cyan-900 tracking-tighter uppercase">Kraunamas robotas...</h3>
            </div>
        );
    }

    const progress = (currentLevelIndex / gameData.levels.length) * 100;

    const getRobotEmoji = () => {
        if (robotDir === 'up') return '⬆️';
        if (robotDir === 'down') return '⬇️';
        if (robotDir === 'left') return '⬅️';
        if (robotDir === 'right') return '➡️';
        return '🤖';
    };

    return (
        <div className="space-y-3 md:space-y-8 animate-slide-up select-none">
            {/* Header / Progress Area */}
            <div className="premium-glass rounded-lg md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-500/5 rounded-full blur-[80px] pointer-events-none"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="w-16 h-16 bg-cyan-100 rounded-2xl flex items-center justify-center text-base md:text-3xl shadow-inner animate-float">
                            🤖
                        </div>
                        <div>
                            <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight">
                                Kodavimo Blokai
                            </h3>
                            <p className="text-slate-500 font-bold">
                                Programuok robotą, kad jis pasiektų tikslą!
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Login</p>
                            <p className="text-2xl font-black text-cyan-600 leading-none">⭐ {score}</p>
                        </div>
                        <div className="bg-white/50 px-6 py-3 rounded-2xl border border-white/50 shadow-inner text-center min-w-[100px]">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Lygis</p>
                            <p className="text-2xl font-black text-slate-800 leading-none">{currentLevelIndex + 1}/{gameData.levels.length}</p>
                        </div>
                    </div>
                </div>

                <div className="mt-6 w-full bg-slate-100 rounded-full h-3 p-1 shadow-inner overflow-hidden">
                    <div 
                        className="h-full bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(34,197,94,0.3)]"
                        style={{ width: `${progress}%` }}
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 md:p-8 items-start">
                {/* Left: Tactical Grid */}
                <div className="lg:col-span-7">
                    <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 lg:p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center">
                        <div className="absolute top-0 right-0 p-3 md:p-8 text-8xl opacity-[0.03] select-none pointer-events-none">🛠️</div>
                        <div className="grid gap-2 p-4 bg-slate-900 rounded-[3.5rem] shadow-2xl border-8 border-slate-800 relative">
                            {currentLevel.grid.map((row, y) => (
                                <div key={y} className="flex gap-2">
                                    {row.map((cell, x) => {
                                        const isRobot = robotPos.x === x && robotPos.y === y;
                                        const isGoal = currentLevel.goal.x === x && currentLevel.goal.y === y;
                                        const isObstacle = cell === 'X';

                                        return (
                                            <div
                                                key={x}
                                                className={`
                                                    w-16 h-16 md:w-20 md:h-20 rounded-2xl flex items-center justify-center text-lg md:text-4xl shadow-inner transition-all duration-500
                                                    ${isObstacle ? 'bg-slate-800 border-2 border-slate-700 shadow-none' : 
                                                      isGoal ? 'bg-emerald-500/20 border-4 border-emerald-500 animate-pulse shadow-[0_0_20px_rgba(16,185,129,0.3)]' : 
                                                      'bg-slate-800/40 border border-slate-700/50'}
                                                    ${isRobot ? 'scale-110 z-20 shadow-2xl' : ''}
                                                `}
                                            >
                                                {isRobot ? (
                                                    <div className="animate-bounce-subtle drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">
                                                        {getRobotEmoji()}
                                                    </div>
                                                ) : isGoal ? (
                                                    <div className="text-base md:text-3xl opacity-80 animate-pulse">🎯</div>
                                                ) : isObstacle ? (
                                                    <div className="text-2xl opacity-20">🚧</div>
                                                ) : null}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Right: Command Center */}
                <div className="lg:col-span-5 space-y-3 md:space-y-8">
                    <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/40 bg-slate-900 relative overflow-hidden flex flex-col min-h-[50vh]">
                        <div className="flex justify-between items-center mb-8">
                            <h4 className="text-[10px] font-black text-cyan-400 uppercase tracking-[0.4em]">Komandų Seka</h4>
                            <div className="bg-slate-800 px-3 py-1 rounded-full text-xs font-black text-slate-400 font-mono">
                                {commands.length}/{currentLevel.max_commands}
                            </div>
                        </div>

                        {/* Visual Command Queue */}
                        <div className="flex-1 bg-slate-800/50 rounded-xl md:rounded-[3rem] p-3 md:p-8 border-2 border-slate-700/50 mb-10 overflow-y-auto custom-scrollbar shadow-inner flex flex-wrap content-start gap-3">
                            {commands.length === 0 ? (
                                <div className="w-full h-full flex flex-col items-center justify-center opacity-20 italic font-serif">
                                    <p className="text-white text-xl">Laukiama instrukcijų...</p>
                                </div>
                            ) : (
                                commands.map((cmd, idx) => (
                                    <div key={idx} className="w-16 h-16 bg-cyan-500 text-white rounded-2xl flex items-center justify-center text-2xl shadow-lg animate-pop-in border-b-4 border-cyan-700">
                                        {cmd === 'forward' && '⬆️'}
                                        {cmd === 'turn_left' && '↪️'}
                                        {cmd === 'turn_right' && '↩️'}
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Controls */}
                        <div className="space-y-6">
                            <div className="grid grid-cols-3 gap-4">
                                <button
                                    onClick={() => addCommand('turn_left')}
                                    disabled={isExecuting || commands.length >= currentLevel.max_commands}
                                    className="premium-button-3d py-6 bg-slate-800 text-white rounded-3xl border-slate-700 active:bg-slate-700 disabled:opacity-20"
                                >
                                    <div className="text-base md:text-3xl">↪️</div>
                                    <div className="text-[8px] font-black uppercase tracking-widest mt-2">Kairėn</div>
                                </button>
                                <button
                                    onClick={() => addCommand('forward')}
                                    disabled={isExecuting || commands.length >= currentLevel.max_commands}
                                    className="premium-button-3d py-6 bg-cyan-600 text-white rounded-3xl border-cyan-800 active:bg-cyan-700 disabled:opacity-20"
                                >
                                    <div className="text-base md:text-3xl">⬆️</div>
                                    <div className="text-[8px] font-black uppercase tracking-widest mt-2">Pirmyn</div>
                                </button>
                                <button
                                    onClick={() => addCommand('turn_right')}
                                    disabled={isExecuting || commands.length >= currentLevel.max_commands}
                                    className="premium-button-3d py-6 bg-slate-800 text-white rounded-3xl border-slate-700 active:bg-slate-700 disabled:opacity-20"
                                >
                                    <div className="text-base md:text-3xl">↩️</div>
                                    <div className="text-[8px] font-black uppercase tracking-widest mt-2">Dešinėn</div>
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={removeLastCommand}
                                    disabled={isExecuting || commands.length === 0}
                                    className="premium-button-3d py-5 bg-rose-900/40 text-rose-300 rounded-3xl border-rose-900/60 active:bg-rose-900/60 disabled:opacity-20 flex items-center justify-center gap-3"
                                >
                                    <span className="text-xl">⬅️</span>
                                    <span className="text-xs font-black uppercase tracking-widest">Atšaukti</span>
                                </button>
                                <button
                                    onClick={executeCommands}
                                    disabled={isExecuting || commands.length === 0}
                                    className="premium-button-3d py-5 bg-emerald-600 text-white rounded-3xl border-emerald-800 active:bg-emerald-700 disabled:opacity-20 flex items-center justify-center gap-3"
                                >
                                    <span className="text-xl">▶️</span>
                                    <span className="text-xs font-black uppercase tracking-widest">Vykdyti</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Performance Overlay */}
            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[1000] p-2 md:p-6">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-2xl w-full p-4 md:p-16 rounded-2xl md:rounded-[4rem] bg-white shadow-[0_40px_100px_rgba(0,0,0,0.5)] relative animate-pop-in border-[12px]
                        ${feedback.type === 'success' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-4 md:p-14 left-1/2 -translate-x-1/2 w-28 h-28 bg-white rounded-full flex items-center justify-center text-2xl md:text-6xl shadow-2xl border-8 border-inherit animate-bounce">
                            {feedback.type === 'success' ? '🚀' : '🤖'}
                        </div>
                        
                        <div className="text-center space-y-3 md:space-y-8">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback.type === 'success' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback.type === 'success' ? 'PROGRAMA VEIKIA!' : 'SISTEMOS KLAIDA'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-left italic font-serif leading-relaxed ${feedback.type === 'success' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-2xl font-bold text-slate-700 text-center leading-relaxed">
                                    {feedback.message}
                                </p>
                            </div>

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-3 h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-sm font-black text-slate-300 uppercase tracking-[0.3em] ml-2">Persikrauname...</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AICodingBlocksGame;
